import './ServiceEditDialog.less';

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components';
import { IFinOpsBudgetAllocationCombined, IFinOpsMonthlyBudgetAllocationRecord, MonthKey } from '../../../models/FinOpsBudgets';
import React, { useEffect, useMemo, useState } from 'react';

import { currencyFormatter } from '../../../utils/currency';
import { useUpdateMonthlyBudgetAllocation } from '../../../hooks/useFinOpsBudget';

interface ServiceEditDialogProps {
  open: boolean;
  onClose: () => void;
  data: IFinOpsBudgetAllocationCombined[];
  fiscalYearStart: Date;
  fiscalYearEnd: Date;
  currentFiscalYear: string;
}

const ServiceEditDialog: React.FC<ServiceEditDialogProps> = ({
  open,
  onClose,
  data,
  fiscalYearStart,
  fiscalYearEnd,
  currentFiscalYear
}) => {
  const cleanedData = React.useMemo(() => {
      return data
          .filter(
          (item): item is Required<Pick<typeof item, 'category' | 'type' | 'month'>> &
              IFinOpsBudgetAllocationCombined =>
              !!item.category && !!item.type && !!item.month
          )
          .map((item) => ({
          ...item,
          category: item.category ?? 'Unknown Category',
          type: item.type ?? 'Unknown Type',
          month: item.month ?? 'Unknown Month',
          budget: Math.round(item.budget ?? 0),
          cost: Math.round(item.cost ?? 0),
          }));
      }, [data]);
  const serviceBudget = React.useMemo(() => { 
    return cleanedData.reduce((sum, item) => sum + (item.budget ?? 0), 0);
  }, [cleanedData]);

  const { 
    mutate: updateBudgetAllocations, 
    isLoading: isSaving, 
    isError, 
    error: updateError,
    isSuccess
  } = useUpdateMonthlyBudgetAllocation(
    () => {
      // Handle success
      setHasUnsavedChanges(false);
      onClose();
      if (window.confirm('Budget allocations updated successfully. Click OK to refresh the page.')) {
          window.location.reload();
      }
    },
    (error) => {
      // Error is handled via isError and error states
      console.error('Failed to update budget allocations:', error);
    }
  );

  const groupedData = React.useMemo(() => {
  const map = new Map<string, Map<string, IFinOpsBudgetAllocationCombined[]>>();
    cleanedData.forEach((item) => {
        if (!map.has(item.category)) {
        map.set(item.category, new Map());
        }
        const typeMap = map.get(item.category)!;
        if (!typeMap.has(item.type)) {
        typeMap.set(item.type, []);
        }
        typeMap.get(item.type)!.push(item);
    });
    return map;
    }, [cleanedData]);

    const months = React.useMemo(() => {
      const monthSet = new Set<string>();
      cleanedData.forEach((item) => {
        const monthDate = new Date(item.month);
        if (fiscalYearStart <= monthDate && monthDate <= fiscalYearEnd) {
          monthSet.add(item.month);
        }
      });
      return Array.from(monthSet).sort();
    }, [cleanedData, fiscalYearStart, fiscalYearEnd]);

    const [editedBudgets, setEditedBudgets] = useState<Record<string, number>>(() => {
      const initial: Record<string, number> = {};
      cleanedData.forEach(({ category, type, month, budget }) => {
          initial[`${category}|${type}|${month}`] = budget;
      });
      return initial;
    });

    const originalBudgets = useMemo(() => {
      const result: Record<string, number> = {};
      cleanedData.forEach(({ category, type, month, budget }) => {
        result[`${category}|${type}|${month}`] = budget;
      });
      return result;
    }, [cleanedData]);

    const totalEditedBudget = useMemo(() => {
      return Object.values(editedBudgets).reduce((a, b) => a + b, 0);
    }, [editedBudgets]);

    const isSaveDisabled = totalEditedBudget !== serviceBudget;

    const onBudgetChange = (category: string, type: string, month: string, value: string) => {
      const key = `${category}|${type}|${month}`;
      const num = Number(value);
      if (isNaN(num) || num < 0) return;
      setEditedBudgets((prev) => ({ ...prev, [key]: num }));
    };

  const onSave = () => {
    if (isSaveDisabled) {
      alert(`Total budget must equal service original budget:${currencyFormatter(serviceBudget)}`);
      return;
    }

    const grouped: Record<string, { [month: string]: number }> = {};
    Object.entries(editedBudgets).forEach(([key, value]) => {
      const [category, type, month] = key.split('|');
      const groupKey = `${category}|${type}`;
      if (!grouped[groupKey]) grouped[groupKey] = {};
      grouped[groupKey][month] = value;
    });

    const allocations: IFinOpsMonthlyBudgetAllocationRecord[] = [];

      Object.entries(grouped).forEach(([groupKey, monthMap]) => {
        const [category, type] = groupKey.split('|');

        const isGroupModified = Object.entries(monthMap).some(([month, val]) => {
          const key = `${category}|${type}|${month}`;
          return val !== originalBudgets[key];
        });

        if (!isGroupModified) return;

        const allocation: IFinOpsMonthlyBudgetAllocationRecord = {
          version: new Date(),
          fiscalYear: currentFiscalYear,
          serviceId: data[0]?.serviceId || '',
          category,
          type,
          submitter: '',
          month1: 0, month2: 0, month3: 0, month4: 0,
          month5: 0, month6: 0, month7: 0, month8: 0,
          month9: 0, month10: 0, month11: 0, month12: 0
        };

        Object.entries(monthMap).forEach(([month, val]) => {
          const monthDate = new Date(month);
          const calendarMonth = monthDate.getMonth(); // 0-11
          const monthNumber = ((calendarMonth + 12 - 5) % 12) + 1;
          allocation[`month${monthNumber}` as MonthKey] = val;
        });

      allocations.push(allocation);
    });

    if (allocations.length === 0) {
      alert('No changes detected.');
      return;
    }
    
    updateBudgetAllocations(allocations);
  };

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    useEffect(() => {
      const original: Record<string, number> = {};
      cleanedData.forEach(({ category, type, month, budget }) => {
        original[`${category}|${type}|${month}`] = budget;
      });
      const changed = Object.keys(original).some((key) => original[key] !== editedBudgets[key]);
      setHasUnsavedChanges(changed);
    }, [editedBudgets, cleanedData]);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleDialogClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmDialog(true);
    } else {
      onClose();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleDialogClose();
      }
    };
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, hasUnsavedChanges]);


  const remaining = serviceBudget - totalEditedBudget;

  return (
    <>
    <Dialog
      open={open}
      modalType="modal"
      onOpenChange={(e, data) => {
        if (!data.open) handleDialogClose();
      }}
    >
      <DialogSurface style={ { width: '95%', maxWidth: '95%',maxHeight: '95vh', height: '95vh', display: 'flex', flexDirection: 'column'} }>
        <DialogBody style={{ height: '100%' }}>
          <DialogTitle>
            <h2>Service Budget Edit</h2>
            <h3>Remaining allocatable budget: {currencyFormatter(remaining)}</h3>
          </DialogTitle> 
          <DialogContent style={{ overflowY: 'auto', flex: 1 }}>
            {[...groupedData.entries()].map(([category, typeMap]) => (
              <div key={category} className="categoryBlock">
                <h3 className="categoryTitle">{category}</h3>
                {[...typeMap.entries()].map(([type, items]) => (
                  <div key={type} className="typeBlock">
                    <h4 className="typeTitle">{type}</h4>
                    <Table aria-label={`${category} ${type} budget table`} className="budgetTable">
                      <TableHeader>
                        <TableRow>
                          <TableHeaderCell className="firstCol">Metric</TableHeaderCell>
                          {months.map((month) => (
                            <TableHeaderCell className="firstCol" key={month}>{new Date(month).toLocaleString('en-US', {month: 'long', year: 'numeric'})}</TableHeaderCell>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="firstCol">Cost</TableCell>
                          {months.map((month) => {
                            const item = items.find((i) => i.month === month);
                            return (
                              <TableCell
                                key={month}
                                style={{
                                    maxWidth: '80px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}>
                                {item && typeof item.cost === 'number' ? currencyFormatter(item.cost) : '-'}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        <TableRow>
                          <TableCell className="firstCol">Budget</TableCell>
                          {months.map((month) => {
                            const key = `${category}|${type}|${month}`;
                            const isModified = editedBudgets[key] !== originalBudgets[key];
                            return (
                              <TableCell key={month}>
                                <Input
                                  type="number"
                                  value={editedBudgets[key]?.toString() ?? '0'}
                                  onChange={(e) =>
                                    onBudgetChange(category, type, month, e.target.value)
                                  }
                                  style={{ width: '80%', backgroundColor: isModified ? '#ffe6e6' : undefined }}
                                  className="budgetInput"
                                  contentBefore={<span>$</span>}
                                  title={isModified ? `Original: $${originalBudgets[key]}` : undefined}
                                />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                ))}
              </div>
            ))}
          </DialogContent>
          <DialogActions style={{ marginTop: '20px', justifyContent: 'flex-end' }}>
            <Button appearance="secondary" onClick={handleDialogClose}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={onSave}
              disabled={isSaveDisabled}
            >
              Save
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
    <Dialog open={showConfirmDialog} onOpenChange={() => setShowConfirmDialog(false)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogContent>
              You have unsaved changes. Do you want to save before exiting?
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => {
                if (isSaveDisabled) {
                  alert(`Total budget must equal service original budget: ${currencyFormatter(serviceBudget)}`);
                  return;
                }
                onClose();
                setShowConfirmDialog(false);
              }}>Save</Button>
              <Button appearance="secondary" onClick={() => {
                setShowConfirmDialog(false);
                onClose();
              }}>Exit Without Saving</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
    </Dialog>
    </>
  );
};

export default ServiceEditDialog;
